import React, {useMemo} from "react";
import styled from "styled-components";
import {decimalFormat} from "../../common/StringUtils";
import {calculateEcpm, formatEcpm} from "../../pages/reports/entity/metrics";
import {getReportMetricColor, REPORT_METRIC_TONES} from "./ReportMetricTheme";

const revenueFormat = value => Number(value || 0).toLocaleString("ko-KR", {
  maximumFractionDigits:1
});

export default function PerformanceComparisonChart({data = [], nameKey, title, description}) {
  const rows = useMemo(() => [...data]
      .sort((a,b) => (Number(b.revenueAmount)||0) - (Number(a.revenueAmount)||0))
      .slice(0,5), [data]);
  const summary = useMemo(() => rows.reduce((sum,item) => ({
    exposureCount:sum.exposureCount+(Number(item.exposureCount)||0),
    revenueAmount:sum.revenueAmount+(Number(item.revenueAmount)||0),
  }),{exposureCount:0,revenueAmount:0}),[rows]);
  const maxExposure = Math.max(...rows.map(item => Number(item.exposureCount)||0),1);
  const maxRevenue = Math.max(...rows.map(item => Number(item.revenueAmount)||0),1);

  return <ChartWrap>
    <ChartHeader>
      <div><ChartTitle>{title}</ChartTitle><ChartDescription>{description}</ChartDescription></div>
      <Kpis>
        <Kpi $tone="exposureCount"><i>노출</i><div><small>총 노출수</small><strong>{decimalFormat(summary.exposureCount)}</strong><em>건</em></div></Kpi>
        <Kpi $tone="revenueAmount"><i>수익</i><div><small>총 수익금</small><strong>{revenueFormat(summary.revenueAmount)}</strong><em>원</em></div></Kpi>
        <Kpi $tone="ecpm"><i>eCPM</i><div><small>평균 eCPM</small><strong>{formatEcpm(calculateEcpm(summary.revenueAmount,summary.exposureCount))}</strong><em>원</em></div></Kpi>
      </Kpis>
    </ChartHeader>
    <Legend><LegendItem $metric="exposureCount"><i/>노출수</LegendItem><LegendItem $metric="revenueAmount"><i/>수익금</LegendItem></Legend>
    <Rows>
      {rows.length ? rows.map((item,index) => <Row key={item.inventoryId || item.userId || index}>
        <Name title={item[nameKey]}>{item[nameKey] || "-"}</Name>
        <Bars>
          <BarLine><Label>노출</Label><Track><Fill $metric="exposureCount" $width={(Number(item.exposureCount)||0)/maxExposure*100}/></Track><Value>{decimalFormat(item.exposureCount)}</Value></BarLine>
          <BarLine><Label>수익</Label><Track><Fill $metric="revenueAmount" $width={(Number(item.revenueAmount)||0)/maxRevenue*100}/></Track><Value>{revenueFormat(item.revenueAmount)}</Value></BarLine>
        </Bars>
      </Row>) : <Empty>조회된 성과 데이터가 없습니다.</Empty>}
    </Rows>
  </ChartWrap>
}

const ChartWrap=styled.div`padding:18px 20px 20px;`;
const ChartHeader=styled.div`display:flex;align-items:flex-start;justify-content:space-between;gap:20px;@media(max-width:900px){flex-direction:column;}`;
const ChartTitle=styled.strong`display:block;color:#27313c;font-size:13px;font-weight:750;`;
const ChartDescription=styled.span`display:block;margin-top:5px;color:#8a949f;font-size:10px;`;
const Kpis=styled.div`display:grid;grid-template-columns:repeat(3,minmax(130px,1fr));gap:7px;@media(max-width:700px){width:100%;grid-template-columns:1fr;}`;
const palette=REPORT_METRIC_TONES;
const Kpi=styled.div`display:flex;height:50px;align-items:center;gap:8px;padding:7px 9px;border:1px solid ${({$tone})=>palette[$tone][2]};border-radius:8px;background:${({$tone})=>palette[$tone][1]};i{display:flex;width:34px;height:30px;align-items:center;justify-content:center;border-radius:7px;background:#fff;color:${({$tone})=>palette[$tone][0]};font-size:8px;font-style:normal;font-weight:800;}small{display:block;color:#7f8995;font-size:8px;}strong{color:#293644;font-size:12px;}em{margin-left:3px;color:#7f8995;font-size:8px;font-style:normal;}`;
const Legend=styled.div`display:flex;gap:18px;margin:17px 0 12px;font-size:10px;font-weight:700;color:#596573;`;
const LegendItem=styled.span`display:flex;align-items:center;gap:6px;i{width:8px;height:8px;border-radius:2px;background:${({$metric})=>getReportMetricColor($metric)};}`;
const Rows=styled.div`display:flex;flex-direction:column;gap:12px;`;
const Row=styled.div`display:grid;grid-template-columns:190px minmax(0,1fr);align-items:center;gap:18px;@media(max-width:700px){grid-template-columns:100px minmax(0,1fr);}`;
const Name=styled.strong`overflow:hidden;color:#384454;font-size:10px;text-overflow:ellipsis;white-space:nowrap;`;
const Bars=styled.div`display:flex;flex-direction:column;gap:7px;`;
const BarLine=styled.div`display:grid;grid-template-columns:28px minmax(0,1fr) 62px;align-items:center;gap:8px;`;
const Label=styled.span`color:#8a949f;font-size:9px;`;
const Track=styled.div`height:7px;overflow:hidden;border-radius:99px;background:#edf0f3;`;
const Fill=styled.div`width:${({$width})=>Math.max($width,0)}%;height:100%;border-radius:99px;background:${({$metric})=>getReportMetricColor($metric)};transition:width .3s ease;`;
const Value=styled.span`color:#485463;font-size:9px;text-align:right;`;
const Empty=styled.div`display:flex;height:120px;align-items:center;justify-content:center;color:#9aa3ad;font-size:10px;`;
